import { defineStore } from "pinia";
import { ref } from "vue";

const SHOW_DELAY_MS = 250;
const MIN_VISIBLE_MS = 320;

export const useAppLoaderStore = defineStore("appLoader", () => {
  const visible = ref(false);

  let showTimer = null;
  let hideTimer = null;
  let shownAt = 0;
  let navigationDepth = 0;

  function clearShowTimer() {
    if (showTimer) {
      window.clearTimeout(showTimer);
      showTimer = null;
    }
  }

  function clearHideTimer() {
    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }
  }

  function show() {
    clearShowTimer();
    clearHideTimer();
    visible.value = true;
    shownAt = Date.now();
  }

  function hideWithMinVisible() {
    if (!visible.value) {
      return;
    }

    const elapsed = shownAt ? Date.now() - shownAt : MIN_VISIBLE_MS;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);

    clearHideTimer();
    hideTimer = window.setTimeout(() => {
      hideTimer = null;
      hide();
    }, remaining);
  }

  /** Full reload / first entry — no navigation delay. */
  function showInitialRoute() {
    clearShowTimer();
    clearHideTimer();
    show();

    if (typeof window.dismissBootSplash === "function") {
      window.dismissBootSplash();
    }
  }

  function finishInitialRoute() {
    clearShowTimer();
    hideWithMinVisible();
  }

  function hide() {
    clearShowTimer();
    clearHideTimer();
    visible.value = false;
    shownAt = 0;
  }

  /** Delay showing until navigation exceeds SHOW_DELAY_MS. */
  function beginNavigation() {
    navigationDepth += 1;
    clearHideTimer();

    if (visible.value || showTimer) {
      return;
    }

    showTimer = window.setTimeout(() => {
      showTimer = null;
      if (navigationDepth > 0) {
        show();
      }
    }, SHOW_DELAY_MS);
  }

  /** Hide after navigation completes; skip flash if loader never appeared. */
  function endNavigation() {
    navigationDepth = Math.max(0, navigationDepth - 1);
    clearShowTimer();

    if (navigationDepth > 0 || !visible.value) {
      return;
    }

    hideWithMinVisible();
  }

  return {
    visible,
    show,
    hide,
    beginNavigation,
    endNavigation,
    showInitialRoute,
    finishInitialRoute,
  };
});
